// CN-workoutstarter-twin: one start orchestration, platform seams injected.
export { useWorkoutStarter } from "../Logger/useWorkoutStarter";
export type {
  StartBuiltSessionInput,
  WorkoutStarterSeams,
} from "../Logger/useWorkoutStarter";
export { useTodayAnyway, type TodayEmptyHost } from "../Logger/useTodayAnyway";
export { useTodayEmptyState } from "../Logger/TodayEmptyProbe";

// THE Logger (RW-18 - moved from apps/mobile/src/logger + apps/mobile/src/
// screens/LoggerScreen.tsx). One tree for mobile's Timeline logger AND the
// web-rnw logger; the host mount supplies everything the shared tree can't
// reach itself (Profile, durable write, the exercise-catalog cache, the
// host-specific picker/timer-sheet/effort-scale components, two optional
// native-only slots) via the `deps: LoggerHostDeps` prop - see
// Logger/hostDeps.ts for the full contract and why each piece is shaped
// the way it is.
export { LoggerScreen } from "../Logger/LoggerScreen";
export type { EditingTarget, MaxBumpInput } from "../Logger/LoggerScreen";
export type {
  ExerciseTimerSheetProps,
  IdleAnswer,
  IdleWatchSeam,
  LoggerCatalogAdapter,
  LoggerExercisePickerProps,
  LoggerHostDeps,
} from "../Logger/hostDeps";
export type { PostSessionSummaryHandle } from "../Logger/usePostSaveSummary";
// HI-3 slice 2b - the foreground timer cues. The event type is what a host's
// `playTimerCue` implementation switches on.
export { usePhaseCues } from "../Logger/usePhaseCues";
export type { TimerCueEvent, TimerCuePhase } from "../Logger/usePhaseCues";
// AN-2 - the animation ⇄ bodymap band. Exported because the seam type is
// what a host mount implements, and the component is what its test renders.
export { ExerciseVisualPager } from "../Logger/ExerciseVisualPager";
export type {
  AnimationSourceFn,
  ExerciseVisualPagerProps,
} from "../Logger/ExerciseVisualPager";
export { ShareCardRenderer } from "../Logger/ShareCardRenderer";
export type { ShareCardRendererProps } from "../Logger/ShareCardRenderer";
export { useShareCardHandle } from "../Logger/useShareCardHandle";
export { useShareCardData } from "../Logger/useShareCardData";
export { OffscreenCapture } from "../Logger/OffscreenCapture";
export { useExerciseName, useExerciseNameBySlug } from "../Logger/exerciseName";
// The draft data model + AsyncStorage/localStorage mirror - read by a
// handful of mobile screens adjacent to the logger (boot/quarantine
// recovery, the session loader, crash-context tagging), which is why
// these stay exported rather than folded fully private into Logger/.
export { useLoggerDraft } from "../Logger/state/loggerDraft";
export type { LoggerDispatch } from "../Logger/state/loggerDraft";
export type { LoggerDraft } from "../Logger/state/loggerDraftTypes";
export {
  hasCommittedWork,
  isNonTrivialDraft,
  parsePersistedBlob,
  shouldPersistDraft,
} from "../Logger/state/loggerDraftPersistence";
export type { PersistedLoggerState } from "../Logger/state/loggerDraftPersistence";
export {
  clearPersistedLoggerState,
  loadPersistedLoggerState,
  savePersistedLoggerState,
  quarantinePrefix,
  liveDraftKey,
  setAsideLiveDraft,
  setLoggerDraftScope,
} from "../Logger/state/loggerDraftStorage";
export { isOwnDraft, setLoggerDraftOwner } from "../Logger/state/draftOwner";
// Per-device preference stores whose Settings-card reader lives outside
// the Logger (mobile: HintsSettingsCard, AudioSettingsCard).
export {
  getRestAudioPreference,
  loadRestAudioPreference,
  useRestAudioPreference,
} from "../Logger/state/restSoundPreference";
export { useEffortNudgeEnabled } from "../Logger/state/effortNudgePreference";
export { useHintsEnabled } from "../Logger/state/hintsPreference";
export { useInWorkoutMeterEnabled } from "../Logger/state/inWorkoutMeterPreference";
// LG-49 - the history pill's blink. Default ON; silences the pulse, never the age.
export { useExerciseAgePulseEnabled } from "../Logger/state/exerciseAgePulsePreference";
export { useStoredEnergyReadout } from "../Logger/storedEnergyReadout";
// Pure logger-adjacent helpers read by mobile's ExercisePicker (recency),
// the boot/quarantine recovery flow, the session loader (edit gating),
// and the mobile draft reducer (loggerNestedReducer, itself moved here).
export { ensureRecentWorkoutsCached } from "../Logger/useEntryHistory";
// LG-49 - pure normaliser: the two history doors emit ONE row shape.
export {
  HISTORY_SET_ROWS,
  lastPerformedAtMs,
  rowsFromLogSets,
  rowsFromSessions,
  type HistorySetRow,
} from "../Logger/historySetRows";
export {
  buildPayload,
  canEditOnMobile,
} from "../Logger/workoutMapping";
export { kindNeedsDuration } from "../Logger/deriveEntryKind";
export { rescaleEntriesToWindow } from "../Logger/timelinePlacement";
// The two logger-exclusive host-mount components RestDefaultsSettingsCard
// shares a PATCH with (useTimerDefaults) and nothing else needs directly.
export { saveExerciseTimerDefaults } from "../Logger/exerciseTimerDefaults";
// Small logger-adjacent primitives read by mobile screens outside the
// Logger tree (Progress/Catalog date rows, the Settings timer sheets, the
// Catalog pain dot's sibling).
export { formatDayMonthYear, formatShortDate } from "@heraion/shared";
export { machineLabel } from "../Logger/machineLabel";
export { locationLabel } from "../Logger/locationLabel";
export { RestStepper } from "../Logger/RestStepper";
// RFC4122 v4 UUID (crypto.getRandomValues-backed) - read by several mobile
// modules well outside the Logger (api client, outbox, profile writes,
// measurements, Health Connect sync); moved here at RW-18 since it's the
// same tiny, dependency-free, fully portable utility on both platforms.
export { uuidv4 } from "../Logger/uuid";
